import { BlogArticleType } from 'types/interfaces/blog';

/**
 * URL state for the public /articles page.
 *
 * The URL is the state: `page` (integer >= 1, absent means 1), `q` (trimmed,
 * absent when empty) and `type` (`guides`, absent for Blog). These helpers own
 * exactly those three keys and never touch anything else in the query string,
 * so utm and referral params survive paging and searching.
 *
 * The list endpoint clamps `limit` at 50, so ARTICLES_PAGE_SIZE must stay at or
 * below that. (The crawler index at /api/v1/meta/articles is unpaginated and lists
 * every published article, so nothing there depends on this value.)
 */
export const ARTICLES_PAGE_SIZE = 24;

/** Exact copy ruled in the plan review; pinned by a unit test. */
export const ARTICLES_ERROR_COPY = 'Articles are unavailable right now. Try again in a moment.';

export const ARTICLES_PATH = '/articles';

const PAGE_KEY = 'page';
const QUERY_KEY = 'q';
const TYPE_KEY = 'type';
const GUIDES_TYPE_VALUE = 'guides';

/** A positive integer page, or 1 for anything absent, non-numeric, zero or negative. */
export const parseArticlesPage = (raw: string | null): number => {
  if (raw === null) return 1;
  if (!/^\d+$/.test(raw.trim())) return 1;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

/**
 * True when the raw `page` value is present but is not the canonical spelling of
 * a page >= 2 (so `page=1`, `page=0`, `page=abc`, `page=02`). Such URLs are
 * normalized to the bare form.
 */
export const isNonCanonicalPageParam = (raw: string | null): boolean => {
  if (raw === null) return false;
  const parsed = parseArticlesPage(raw);
  return parsed === 1 || String(parsed) !== raw;
};

export const parseArticlesQuery = (raw: string | null): string => (raw ?? '').trim();

/** `type=guides` selects Guides; anything else (including absent) is Blog. */
export const parseArticlesType = (raw: string | null): BlogArticleType =>
  raw !== null && raw.trim().toLowerCase() === GUIDES_TYPE_VALUE ? 'GUIDE' : 'BLOG';

/** True when a `type` value is present but is not exactly `guides`; it gets dropped. */
export const isNonCanonicalTypeParam = (raw: string | null): boolean =>
  raw !== null && raw !== GUIDES_TYPE_VALUE;

export interface ArticlesSearchPatch {
  page?: number;
  q?: string;
  type?: BlogArticleType;
}

/**
 * Copy `prev`, apply the patch to the keys this page owns, drop defaults
 * (page 1, empty q, Blog type) and return the query string with a leading `?`,
 * or '' when nothing remains.
 */
export const buildArticlesSearch = (prev: URLSearchParams, patch: ArticlesSearchPatch): string => {
  const params = new URLSearchParams(prev);

  if (patch.page !== undefined) {
    if (patch.page > 1) params.set(PAGE_KEY, String(patch.page));
    else params.delete(PAGE_KEY);
  }

  if (patch.q !== undefined) {
    const q = patch.q.trim();
    if (q) params.set(QUERY_KEY, q);
    else params.delete(QUERY_KEY);
  }

  if (patch.type !== undefined) {
    if (patch.type === 'GUIDE') params.set(TYPE_KEY, GUIDES_TYPE_VALUE);
    else params.delete(TYPE_KEY);
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
};

/** Href for a pager anchor: keeps q and type, sets page, bare /articles for page 1. */
export const pageHref = (prev: URLSearchParams, page: number): string =>
  `${ARTICLES_PATH}${buildArticlesSearch(prev, { page })}`;

/** Count line above the grid: `79 articles`, `1 article`, `3 articles for "landstorm"`. */
export const formatArticlesCount = (total: number, q: string): string => {
  const noun = total === 1 ? 'article' : 'articles';
  const base = `${total.toLocaleString()} ${noun}`;
  const query = q.trim();
  return query ? `${base} for “${query}”` : base;
};

/** Title suffix for pages past the first; page 1 keeps the plain title. */
export const articlesPageTitle = (page: number): string =>
  page > 1 ? `Articles & Resources (page ${page}) - TradeMagic` : 'Articles & Resources - TradeMagic';
